import React, { createContext, ReactNode, useContext, ReactElement } from 'react';
import { StyleProp, ViewStyle } from 'react-native';
import Animated, {
    withRepeat,
    withSequence,
    withTiming,
    useAnimatedStyle,
    Easing,
    FadeOut,
    FadeInUp,
    AnimatedStyle,
} from 'react-native-reanimated';
import { useThemeContext } from '_utils/themeContext';

interface SkeletonLoaderProps {
    variant?: 'foreground' | 'background' | 'container' | 'provider';
    style?: StyleProp<ViewStyle>;
    children?: ReactNode;
    index?: number;
}

const SkeletonLoader = ({ variant = 'background', style, children, index = 0 }: SkeletonLoaderProps): ReactElement => {
    const { theme } = useThemeContext();
    const context = useShimmerStyle();

    const shimmerStyle =
        context ||
        // We know what we are doing here, we guarantee stability around the context existing or not.
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useAnimatedStyle(() => ({
            opacity: withRepeat(
                withSequence(
                    withTiming(0.3, {
                        duration: 1500,
                        easing: Easing.bezier(0.4, 0, 0.2, 1),
                    }),
                    withTiming(0.6, {
                        duration: 1500,
                        easing: Easing.bezier(0.4, 0, 0.2, 1),
                    }),
                ),
                -1,
                true,
            ),
        }));

    return (
        <Animated.View
            entering={variant === 'container' ? FadeInUp.delay(index * 150).springify() : undefined}
            exiting={variant === 'container' ? FadeOut.duration(300) : undefined}
            style={[
                {
                    backgroundColor:
                        variant === 'container' || variant === 'provider'
                            ? undefined
                            : variant === 'foreground'
                              ? theme.mediumGrey
                              : theme.mediumBackground,
                    borderRadius: variant === 'foreground' ? 4 : undefined,
                },
                variant !== 'container' && variant !== 'provider' ? shimmerStyle : undefined,
                style,
            ]}
        >
            {children ? (
                context ? (
                    children
                ) : (
                    <ShimmerContext.Provider value={shimmerStyle}>{children}</ShimmerContext.Provider>
                )
            ) : null}
        </Animated.View>
    );
};

const ShimmerContext = createContext<AnimatedStyle<ViewStyle> | null>(null);

const useShimmerStyle = () => useContext(ShimmerContext);

export default SkeletonLoader;
